import { SafeImage } from "../common/SafeImage";
import React, { useState, useMemo } from "react";
import { addDays, format, getDay } from "date-fns";
import { collection, query, where, getDocs } from "firebase/firestore"; 
import { db } from "../../lib/firebase"; 

import { MealType, CartItem, MenuItem } from "../../types/index";
import ItemSelectorModal from "./ItemSelectorModal";
import { Card } from "../common/Card";
import { Button } from "../common/Button";
import { Calendar, Clock, Copy, Trash2, Plus, Sparkles, Ban, X, AlertCircle, Shuffle, Loader, Store, AlertTriangle } from "lucide-react"; 
import { toast } from "react-hot-toast";

// --- CONSTANTS ---
const BREAKFAST_END_HOUR = 5; 
const BREAKFAST_EDIT_START = 19; 
const LUNCH_12PM_CUTOFF = 10; 
const SNACKS_CUTOFF = 15; 
const DINNER_CUTOFF = 18; // = processDinnerOrders (18:00 IST, MEAL_LOCK_HOUR_IST in functions)

type MealEditWindow = { canEdit: boolean; targetDateKey: string; };

// --- HELPER FUNCTIONS ---

function getMealEditWindow(meal: MealType, cellDate: Date, lunchTime?: "8AM" | "12PM"): MealEditWindow {
  const now = new Date();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayKey = getDateKey(today);
  const cell = new Date(cellDate);
  cell.setHours(0, 0, 0, 0);
  const cellKey = getDateKey(cell);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  const tomorrowKey = getDateKey(tomorrow);

  if (cell.getTime() > tomorrow.getTime()) return { canEdit: true, targetDateKey: cellKey };

  if (meal === "breakfast" || (meal === "lunch" && lunchTime === "8AM")) {
    const sevenPMToday = new Date(today); sevenPMToday.setHours(BREAKFAST_EDIT_START, 0, 0, 0);
    const fiveAMTomorrow = new Date(tomorrow); fiveAMTomorrow.setHours(BREAKFAST_END_HOUR, 0, 0, 0);
    if (cellKey === tomorrowKey && now >= sevenPMToday && now <= fiveAMTomorrow) return { canEdit: true, targetDateKey: tomorrowKey };
    const fiveAMToday = new Date(today); fiveAMToday.setHours(BREAKFAST_END_HOUR, 0, 0, 0);
    if (cellKey === todayKey && now <= fiveAMToday) return { canEdit: true, targetDateKey: todayKey };
    if (cellKey > tomorrowKey) return { canEdit: true, targetDateKey: cellKey };
    return { canEdit: false, targetDateKey: cellKey };
  }
  
  if (meal === "lunch" && lunchTime === "12PM") {
    const tenAMToday = new Date(today); tenAMToday.setHours(LUNCH_12PM_CUTOFF, 0, 0, 0);
    if (cellKey === todayKey && now <= tenAMToday) return { canEdit: true, targetDateKey: todayKey };
    return { canEdit: true, targetDateKey: cellKey };
  }
  if (meal === "evening_snacks") {
    const threePM = new Date(today); threePM.setHours(SNACKS_CUTOFF, 0, 0, 0);
    if (cellKey === todayKey && now <= threePM) return { canEdit: true, targetDateKey: todayKey };
    return { canEdit: true, targetDateKey: cellKey };
  }
  if (meal === "dinner") {
    const sixPM = new Date(today); sixPM.setHours(DINNER_CUTOFF, 0, 0, 0);
    if (cellKey === todayKey && now <= sixPM) return { canEdit: true, targetDateKey: todayKey };
    return { canEdit: true, targetDateKey: cellKey };
  }
  return { canEdit: true, targetDateKey: cellKey };
}

const getVariant = (customizations: any) => {
  if (!customizations) return null;
  if (Array.isArray(customizations)) {
    const found = customizations.find((c: any) => c.label === "Variant" || c.label === "Size" || c.type === "variant");
    return found ? found.value : null;
  }
  return customizations.variant || null;
};

const getDateKey = (date: Date) => format(date, "yyyy-MM-dd");

// --- MAIN COMPONENT ---

interface Props {
  startDate: Date;
  durationDays: number;
  selectedMeals: MealType[];
  onComplete: (payload: {
    selections: Record<string, Record<string, CartItem[]>>;
    startDate: Date;
    durationDays: number;
    lunchTime?: "8AM" | "12PM";
  }) => void;
  kitchenId?: string;
  lunchTime?: "8AM" | "12PM";
}

export const SubscriptionCalendar: React.FC<Props> = ({
  startDate,
  durationDays,
  selectedMeals,
  onComplete,
  kitchenId,
  lunchTime = "12PM",
}) => {
  const dates = useMemo(() => {
    return Array.from({ length: durationDays }).map((_, i) => addDays(startDate, i));
  }, [startDate, durationDays]);

  const [selections, setSelections] = useState<Record<string, Record<string, CartItem[]>>>({});
  const [skippedDays, setSkippedDays] = useState<Set<string>>(new Set());
  
  const [editingSlot, setEditingSlot] = useState<{
    date: Date;
    meal: MealType;
    index: number | "new"; 
  } | null>(null);

  const [showPatternModal, setShowPatternModal] = useState(false);
  const [isFillingRandomly, setIsFillingRandomly] = useState(false);

  const hasPatternReady = useMemo(() => {
     const first5Weekdays = dates.filter((d) => { const day = getDay(d); return day !== 0 && day !== 6; }).slice(0, 5);
     return first5Weekdays.every((date) => {
       const key = getDateKey(date);
       return selections[key] && selectedMeals.every((meal) => selections[key][meal]?.length > 0);
     });
  }, [selections, dates, selectedMeals]);

  // Handle Save Item
  const handleSaveItem = (itemInput: CartItem | CartItem[]) => {
    if (!editingSlot) return;
    const itemsToAdd = Array.isArray(itemInput) ? itemInput : [itemInput];
    const dateKey = getDateKey(editingSlot.date);

    setSelections((prev) => {
      const dayData = prev[dateKey] || {};
      const currentMealItems = dayData[editingSlot.meal] || [];
      let updatedMealItems = [...currentMealItems];

      if (editingSlot.index === "new") {
        updatedMealItems = [...updatedMealItems, ...itemsToAdd];
      } else {
        updatedMealItems[editingSlot.index] = { ...updatedMealItems[editingSlot.index], ...itemsToAdd[0] };
      }

      return { ...prev, [dateKey]: { ...dayData, [editingSlot.meal]: updatedMealItems } };
    });
    setEditingSlot(null);
  };

  // Remove Item
  const handleRemoveItem = (date: Date, meal: MealType, index: number) => {
    const dateKey = getDateKey(date);
    setSelections(prev => {
        const dayData = prev[dateKey] || {};
        const items = dayData[meal] || [];
        const newItems = items.filter((_, i) => i !== index);
        return { ...prev, [dateKey]: { ...dayData, [meal]: newItems } };
    });
  };

  // Update Quantity
  const updateQuantity = (date: Date, meal: MealType, index: number, newQty: number) => {
    const dateKey = getDateKey(date);
    setSelections(prev => {
        const dayData = prev[dateKey] || {};
        const items = [...(dayData[meal] || [])];
        if (items[index]) {
            items[index] = { ...items[index], quantity: newQty };
        }
        return { ...prev, [dateKey]: { ...dayData, [meal]: items } };
    });
  };

  // Random Fill
  const handleRandomFill = async () => {
    if (!kitchenId) return toast.error("Kitchen ID missing");
    if(!window.confirm("This will overwrite empty slots with random items. Continue?")) return;

    setIsFillingRandomly(true);
    try {
      const itemsRef = collection(db, "menuItems");
      const q = query(itemsRef, where("kitchenId", "==", kitchenId), where("isAvailable", "==", true));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) throw new Error("No menu items found.");

      const allItems = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MenuItem));

      const getItemsForMeal = (meal: MealType) => {
        const lowerMeal = meal.toLowerCase();
        const matches = allItems.filter(item => {
           const cat = (item.category || "").toLowerCase();
           if (lowerMeal.includes("breakfast") && (cat.includes("breakfast") || cat.includes("tiffin"))) return true;
           if (lowerMeal.includes("lunch") && (cat.includes("meal") || cat.includes("rice") || cat.includes("curry"))) return true;
           if (lowerMeal.includes("dinner") && (cat.includes("meal") || cat.includes("curry"))) return true;
           return false;
        });
        return matches.length > 0 ? matches : allItems;
      };

      const newSelections = { ...selections };

      dates.forEach(date => {
        const dateKey = getDateKey(date);
        if (skippedDays.has(dateKey)) return;
        if (!newSelections[dateKey]) newSelections[dateKey] = {};

        selectedMeals.forEach(meal => {
           if (newSelections[dateKey][meal]?.length > 0) return;

           const pool = getItemsForMeal(meal);
           if (pool.length === 0) return;

           const randomMain = pool[Math.floor(Math.random() * pool.length)];
           
           const toCartItem = (m: MenuItem): CartItem => ({
             id: m.id, name: m.name, price: m.price || 0, quantity: 1,
             kitchenId: kitchenId, kitchenName: m.kitchen_name || "Kitchen",
             photoUrl: m.photoUrl, category: m.category,
             customizations: m.customizations && m.customizations.length > 0 ? [{ label: "Variant", value: "Standard" }] : undefined
           });

           newSelections[dateKey][meal] = [toCartItem(randomMain)];
        });
      });

      setSelections(newSelections);
      toast.success("Calendar filled! 🎲");

    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch menu items.");
    } finally {
      setIsFillingRandomly(false);
    }
  };

  const handleRepeat = (sourceDate: Date, meal: MealType) => {
    const sourceKey = getDateKey(sourceDate);
    const itemsToCopy = selections[sourceKey]?.[meal];
    if (!itemsToCopy?.length) return toast.error("Select items first to copy.");
    if (!window.confirm(`Apply ${meal.toUpperCase()} to ALL days?`)) return;
    setSelections(prev => {
        const next = {...prev};
        dates.forEach(d => {
            const k = getDateKey(d);
            if(!skippedDays.has(k)) { if(!next[k]) next[k]={}; next[k][meal] = [...itemsToCopy]; }
        });
        return next;
    });
    toast.success("Copied!");
  };

  const toggleSkipDay = (d: Date) => {
      const k = getDateKey(d);
      setSkippedDays(prev => { const n = new Set(prev); if(n.has(k)) n.delete(k); else n.add(k); return n; });
  };
  const clearDay = (d: Date) => {
      if(!window.confirm("Clear date?")) return;
      const k = getDateKey(d);
      setSelections(prev => { const n = {...prev}; delete n[k]; return n; });
  };

  return (
    <div className="space-y-6 pb-40">
      {/* HEADER BAR */}
      <div className="flex flex-col md:flex-row justify-between items-center bg-white p-6 rounded-xl shadow-sm border border-gray-200 sticky top-20 z-20">
        <div className="mb-6 md:mb-0 md:w-1/3">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3 mb-2">
            <Calendar className="text-orange-600 w-8 h-8" /> Subscription Planner
          </h2>
          <p className="text-gray-600 leading-relaxed text-sm">
             Customize your <strong>{durationDays}-day</strong> meal plan.
          </p>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap justify-end">
           <Button
            onClick={handleRandomFill}
            variant="secondary"
            disabled={isFillingRandomly}
            className="flex items-center gap-2 bg-gradient-to-r from-teal-50 to-emerald-50 border-teal-200 text-teal-700 hover:from-teal-100 hover:to-emerald-100 px-4"
          >
            {isFillingRandomly ? <Loader size={18} className="animate-spin" /> : <Shuffle size={18} />}
            Fill it for me
          </Button>

          {hasPatternReady && (
            <Button
              onClick={() => setShowPatternModal(true)}
              variant="secondary"
              className="flex items-center gap-2 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200 text-purple-700 hover:from-purple-100 hover:to-indigo-100 px-4"
            >
              <Sparkles size={18} />
              Smart Fill
            </Button>
          )}
          
          <Button
            onClick={() => onComplete({ selections, startDate, durationDays, lunchTime })}
            className="px-8 py-3 shadow-lg shadow-orange-200 font-semibold text-lg"
          >
            Confirm & Pay
          </Button>
        </div>
      </div>

      {/* ✅ DISCLAIMER NOTE (Subject to Availability) */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 text-amber-900 text-sm shadow-sm">
         <AlertTriangle className="w-5 h-5 flex-shrink-0 text-amber-600 mt-0.5" />
         <p>
            <strong>Note:</strong> This is only a preference list. Actual items are subject to availability on any given day. 
            If an item is unavailable, we will provide the best possible alternative from the same kitchen.
         </p>
      </div>

      {/* GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {dates.map((date) => {
          const dateKey = getDateKey(date);
          const isToday = getDateKey(new Date()) === dateKey;
          const isSkipped = skippedDays.has(dateKey);
          const dayData = selections[dateKey] || {};
          
          return (
             <Card key={dateKey} className={`flex flex-col relative overflow-hidden transition-all duration-300 ${isSkipped ? "opacity-50 bg-gray-50" : "hover:shadow-xl"} ${isToday ? "ring-2 ring-orange-400" : ""}`}>
                 
                 {/* DATE HEADER */}
                 <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <div>
                        <span className="font-bold text-xl">{format(date, "MMM do")}</span>
                        <span className="ml-2 text-sm text-gray-500">{format(date, "EEE")}</span>
                        {isToday && <span className="ml-2 text-[10px] bg-orange-500 text-white px-2 py-0.5 rounded-full">TODAY</span>}
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => toggleSkipDay(date)} className="text-xs bg-white border px-2 py-1 rounded hover:bg-gray-100">{isSkipped ? "Enable" : "Skip"}</button>
                        {Object.keys(dayData).length > 0 && !isSkipped && <button onClick={() => clearDay(date)} className="text-red-500 hover:bg-red-50 p-1 rounded"><X size={16}/></button>}
                    </div>
                 </div>

                 {/* SKIPPED OVERLAY */}
                 {isSkipped && (
                    <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center backdrop-blur-[1px]">
                        <div className="bg-gray-800 text-white px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2">
                            <Ban size={16}/> Skipped
                        </div>
                    </div>
                 )}

                 {/* MEAL LOOPS */}
                 {!isSkipped && <div className="p-4 space-y-6">
                     {selectedMeals.map(meal => {
                         const { canEdit, targetDateKey } = getMealEditWindow(meal, date, lunchTime);
                         const targetData = selections[targetDateKey] || {};
                         const items = targetData[meal] || [];

                         return (
                             <div key={meal} className="relative">
                                 <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-100">
                                     <span className="text-xs font-bold uppercase text-gray-500 flex items-center gap-1">
                                        <Clock size={14}/> {meal.replace("_", " ")}
                                     </span>
                                     {items.length > 0 && canEdit && (
                                         <button onClick={() => handleRepeat(new Date(targetDateKey), meal)} className="text-xs text-indigo-600 flex items-center gap-1 hover:bg-indigo-50 px-2 py-1 rounded">
                                            <Copy size={12}/> Repeat
                                         </button>
                                     )}
                                 </div>

                                 {/* LIST OF SELECTED ITEMS */}
                                 <div className="space-y-3">
                                     {items.map((item, idx) => (
                                         <CartItemRow 
                                            key={`${item.id}_${idx}`} 
                                            item={item} 
                                            canEdit={canEdit}
                                            onUpdateQty={(q) => updateQuantity(new Date(targetDateKey), meal, idx, q)}
                                            onDelete={() => handleRemoveItem(new Date(targetDateKey), meal, idx)}
                                            onEdit={() => setEditingSlot({ date: new Date(targetDateKey), meal, index: idx })}
                                         />
                                     ))}
                                     
                                     {/* ADD ITEM BUTTON */}
                                     {canEdit ? (
                                         <button 
                                            onClick={() => setEditingSlot({ date: new Date(targetDateKey), meal, index: "new" })}
                                            className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-gray-400 text-sm font-medium hover:border-orange-400 hover:text-orange-500 hover:bg-orange-50 transition-all flex items-center justify-center gap-2"
                                         >
                                            <Plus size={16} /> Add Item from Any Kitchen
                                         </button>
                                     ) : (
                                        items.length === 0 && <div className="text-center text-xs text-gray-400 py-2">Cut-off time passed</div>
                                     )}
                                 </div>
                             </div>
                         )
                     })}
                 </div>}
             </Card>
          );
        })}
      </div>

      {Object.keys(selections).length > 0 && <PriceSummary selections={selections} durationDays={durationDays} />}
      
      {editingSlot && (
        <ItemSelectorModal
          isOpen={!!editingSlot}
          onClose={() => setEditingSlot(null)}
          onSelect={handleSaveItem}
          kitchenId={undefined} 
          // ✅ FIX: Cast string to 'any' to satisfy TS (since ItemSelectorModal expects number but works with string at runtime)
          context={{ 
            date: editingSlot.date, 
            meal: editingSlot.meal, 
            slot: (editingSlot.index === "new" ? "New Item" : `Item ${editingSlot.index + 1}`) as any 
          }}
        />
      )}
      
      {showPatternModal && (
        <PatternFillModal
          dates={dates}
          selections={selections}
          selectedMeals={selectedMeals}
          skippedDays={skippedDays}
          onApply={(newSelections) => { setSelections(newSelections); setShowPatternModal(false); }}
          onClose={() => setShowPatternModal(false)}
        />
      )}
    </div>
  );
};

// --- SUB-COMPONENT: Cart Item Row ---
const CartItemRow: React.FC<{
    item: CartItem;
    canEdit: boolean;
    onUpdateQty: (q: number) => void;
    onDelete: () => void;
    onEdit: () => void;
}> = ({ item, canEdit, onUpdateQty, onDelete, onEdit }) => {
    const variant = getVariant(item.customizations);

    return (
        <div className="group flex items-center gap-3 p-3 bg-white border rounded-xl shadow-sm hover:shadow-md transition-all">
            {/* Image */}
            <div className="relative w-12 h-12 flex-shrink-0 cursor-pointer" onClick={canEdit ? onEdit : undefined}>
                <SafeImage src={item.photoUrl} className="w-full h-full object-cover rounded-lg" alt={item.name} />
                {canEdit && <div className="absolute inset-0 bg-black/10 rounded-lg group-hover:bg-black/0 transition-all"/>}
            </div>

            {/* Details */}
            <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-gray-800 truncate">{item.name}</div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><Store size={10}/> {item.kitchenName}</span>
                </div>
                {variant && <span className="text-[10px] bg-gray-100 px-1.5 rounded text-gray-600">{variant}</span>}
            </div>

            {/* Controls */}
            {canEdit && (
                <div className="flex flex-col items-end gap-1">
                    <div className="flex items-center border rounded-lg bg-gray-50">
                        <button onClick={() => onUpdateQty(Math.max(1, item.quantity - 1))} className="px-2 py-0.5 hover:bg-gray-200 rounded-l-lg" disabled={item.quantity <= 1}>-</button>
                        <span className="text-xs font-bold w-4 text-center">{item.quantity}</span>
                        <button onClick={() => onUpdateQty(item.quantity + 1)} className="px-2 py-0.5 hover:bg-gray-200 rounded-r-lg">+</button>
                    </div>
                    <button onClick={onDelete} className="text-red-400 hover:text-red-600 text-[10px] flex items-center gap-1">
                        <Trash2 size={10}/> Remove
                    </button>
                </div>
            )}
            
            {/* Price (if not editing, just show price) */}
            {!canEdit && (
                <div className="font-bold text-sm">₹{item.price * item.quantity}</div>
            )}
        </div>
    )
}

// --- SUB-COMPONENT: PRICE SUMMARY ---
const PriceSummary: React.FC<{
  selections: Record<string, Record<string, CartItem[]>>;
  durationDays: number;
}> = ({ selections, durationDays }) => {
  const { totalPrice, daysWithMeals } = useMemo(() => {
    let total = 0;
    let daysCount = 0;

    Object.values(selections).forEach((dayMeals) => {
      let hasMeal = false;
      Object.values(dayMeals).forEach((items) => {
        if (items.length > 0) hasMeal = true;
        items.forEach(i => total += (i.price || 0) * (i.quantity || 1));
      });
      if (hasMeal) daysCount++;
    });
    return { totalPrice: total, daysWithMeals: daysCount };
  }, [selections]);

  const avgPricePerDay = daysWithMeals > 0 ? totalPrice / daysWithMeals : 0;

  if (daysWithMeals === 0) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-orange-50 to-orange-100 border-t-2 border-orange-300 px-6 py-4 shadow-2xl z-10">
      <div className="max-w-7xl mx-auto grid grid-cols-3 gap-6 text-center sm:text-left">
        <div>
          <p className="text-sm text-gray-700 font-semibold">Days Planned</p>
          <p className="text-3xl font-black text-orange-600">{daysWithMeals}/{durationDays}</p>
        </div>
        <div>
          <p className="text-sm text-gray-700 font-semibold">Avg per Day</p>
          <p className="text-3xl font-black text-orange-600">₹{avgPricePerDay.toFixed(0)}</p>
        </div>
        <div className="border-l-2 border-orange-300 pl-6">
          <p className="text-sm text-gray-700 font-semibold">Total Amount</p>
          <p className="text-4xl font-black text-orange-700">₹{totalPrice.toFixed(0)}</p>
        </div>
      </div>
    </div>
  );
};

// --- PATTERN FILL MODAL ---
const PatternFillModal: React.FC<{
  dates: Date[];
  selections: Record<string, Record<string, CartItem[]>>;
  selectedMeals: MealType[];
  skippedDays: Set<string>;
  onApply: (selections: Record<string, Record<string, CartItem[]>>) => void;
  onClose: () => void;
}> = ({ dates, selections, selectedMeals, skippedDays, onApply, onClose }) => {
  const [skipWeekends, setSkipWeekends] = useState(false);
  const [rotatePattern, setRotatePattern] = useState(true);

  const handleApply = () => {
    // Get first 5 weekdays as pattern
    const patternDays = dates.filter((d) => { const day = getDay(d); return day !== 0 && day !== 6; }).slice(0, 5);
    const pattern = patternDays.map((d) => selections[getDateKey(d)]);
    const newSelections = { ...selections };
    let patternIndex = 0;

    dates.forEach((date) => {
      const dateKey = getDateKey(date);
      const dayOfWeek = getDay(date);
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      if (skipWeekends && isWeekend) return;
      if (skippedDays.has(dateKey)) return;

      if (rotatePattern && pattern.length > 0) {
        const patternData = pattern[patternIndex % pattern.length];
        if (patternData) { newSelections[dateKey] = JSON.parse(JSON.stringify(patternData)); }
        patternIndex++;
      } else if (pattern[0]) {
        newSelections[dateKey] = JSON.parse(JSON.stringify(pattern[0]));
      }
    });

    onApply(newSelections);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-6 backdrop-blur-md">
       <div className="bg-white p-8 rounded-2xl w-full max-w-md shadow-2xl">
           <h2 className="text-2xl font-bold mb-2 flex items-center gap-2"><Sparkles className="text-purple-600"/> Smart Fill</h2>
           <p className="text-gray-500 mb-6">Apply your 5-day pattern to the whole month.</p>
           
           <div className="space-y-4 mb-8">
               <label className="flex items-center gap-3 p-3 border rounded-xl hover:bg-gray-50 cursor-pointer">
                   <input type="checkbox" checked={rotatePattern} onChange={e => setRotatePattern(e.target.checked)} className="w-5 h-5 text-purple-600"/>
                   <span className="font-semibold text-gray-700">Rotate Pattern (Mon-Fri)</span>
               </label>
               <label className="flex items-center gap-3 p-3 border rounded-xl hover:bg-gray-50 cursor-pointer">
                   <input type="checkbox" checked={skipWeekends} onChange={e => setSkipWeekends(e.target.checked)} className="w-5 h-5 text-purple-600"/>
                   <span className="font-semibold text-gray-700">Skip Weekends</span>
               </label>
           </div>
           
           <div className="flex gap-3">
               <Button variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
               <Button onClick={handleApply} className="flex-1">Apply</Button>
           </div>
       </div>
    </div>
  );
};